import React, { useState, useRef } from 'react';
import { BASE_API } from './constants';
import styles from './NurseScheduleCard.module.css';

interface NurseDetails {
  firstName: string;
  lastName: string;
  email: string;
  pictureUrl?: string;
  nickname?: string;
  gender?: string;
  city?: string;
  state?: string;
}

interface CalendarSlot {
  scheduleId: string;
  startTime: string;
  slot: number;
  timeBlock: number;
  status: 'free' | 'busy';
  appointmentId?: string;
  patient?: {
    id: string;
    demographics: {
      fullName?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      nickname?: string;
      pictureUrl?: string;
    };
  };
}

interface CalendarDay {
  day: string;
  slots: CalendarSlot[];
}

interface NurseScheduleCardProps {
  nurseDetails: NurseDetails;
  calendar: CalendarDay[];
  nurseId?: string;
  accessToken?: string;
  onProfileUpdate?: (updated: NurseDetails) => void;
}

const NurseScheduleCard: React.FC<NurseScheduleCardProps> = ({ nurseDetails, calendar, nurseId, accessToken, onProfileUpdate }) => {
  console.log('Schedule calendar data:', calendar);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    firstName: nurseDetails.firstName || '',
    lastName: nurseDetails.lastName || '',
    gender: nurseDetails.gender || '',
    city: nurseDetails.city || '',
    state: nurseDetails.state || '',
    pictureUrl: nurseDetails.pictureUrl || '',
    email: nurseDetails.email || '',
    nickname: nurseDetails.nickname || '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !nurseId || !accessToken) return;
    setUploading(true);
    setError(null);
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('profilePic', file);
    try {
      const res = await fetch(`${BASE_API}/nurses/${nurseId}/upload/picture`, {
        method: 'POST',
        headers: { 'x-access-token': accessToken },
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.link) {
        setForm(f => ({ ...f, pictureUrl: data.link }));
      } else {
        setError(data.error || 'Failed to upload picture');
      }
    } catch (err: any) {
      setError('Failed to upload picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!nurseId || !accessToken) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/nurses/${nurseId}/auth0/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': accessToken,
        },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          gender: form.gender || '',
          city: form.city,
          state: form.state,
          pictureUrl: form.pictureUrl,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setEditMode(false);
        if (onProfileUpdate) onProfileUpdate({
          firstName: form.firstName,
          lastName: form.lastName,
          gender: form.gender || '',
          city: form.city,
          state: form.state,
          pictureUrl: form.pictureUrl,
          email: nurseDetails.email,
          nickname: nurseDetails.nickname,
        });
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (err: any) {
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Helper to compare two dates (YYYY-MM-DD) in local time
  function isSameLocalDay(dateStr: string, ref: Date) {
    const d = new Date(dateStr);
    return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
  }

  const now = new Date();
  const [expandedDay, setExpandedDay] = useState(() => {
    // Default to today if present, else first day
    const todayIdx = calendar.findIndex(day => isSameLocalDay(day.day, now));
    return todayIdx !== -1 ? todayIdx : 0;
  });

  // Chevron SVG
  const Chevron = ({ expanded }: { expanded: boolean }) => (
    <svg width="18" height="18" style={{ transition: 'transform 0.3s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
  );

  return (
    <div className={styles.scheduleCard}>
      <div className={styles.scheduleCardHeader}>
        <h3 className={styles.scheduleCardTitle}>This Week's Schedule</h3>
      </div>
      {calendar.length === 0 ? (
        <div className={styles.scheduleEmpty}>No schedule available.</div>
      ) : (
        <div style={{ width: '100%' }}>
          {calendar.map((day, idx) => (
            <div key={day.day} className={styles.scheduleDay}>
              <div
                className={
                  styles.scheduleDayHeader + ' ' +
                  (isSameLocalDay(day.day, now)
                    ? styles.scheduleDayHeaderToday
                    : styles.scheduleDayHeaderDefault)
                }
                onClick={() => setExpandedDay(idx)}
              >
                <span>
                  {new Date(day.day).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
                  {isSameLocalDay(day.day, now) && <span className={styles.scheduleDayTodayLabel}>(Today)</span>}
                </span>
                <Chevron expanded={expandedDay === idx} />
              </div>
              {expandedDay === idx && (
                <ul className={styles.scheduleSlotList}>
                  {day.slots.length === 0 ? (
                    <li className={styles.scheduleSlotEmpty}>No slots</li>
                  ) : (
                    day.slots
                      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                      .map(slot => (
                        <li key={slot.scheduleId} className={styles.scheduleSlot}>
                          <span className={styles.scheduleSlotTime}>
                            {(() => {
                              const start = new Date(slot.startTime);
                              const duration = slot.slot || 30;
                              return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${duration} min)`;
                            })()}
                          </span>
                          <span className={slot.status === 'busy' ? styles.scheduleSlotStatusBooked : styles.scheduleSlotStatusAvailable}>
                            {slot.status === 'busy' ? 'Booked' : 'Available'}
                          </span>
                        </li>
                      ))
                  )}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NurseScheduleCard; 